import {Inject, Injectable, InjectionToken, OnDestroy, signal} from '@angular/core';
import {Observable, Subject, Subscription} from 'rxjs';
import * as signalR from '@microsoft/signalr';
import {HubConnectionState} from '@microsoft/signalr';
import {AuthenticationService} from './authentication.service';
import {UserJwt} from '../models/admin_owner/user/user-jwt';

/**
 * The server → client method the gates hub pushes on. One method for every gate, deliberately.
 *
 * The client does not branch on the reason and must never start to: the only correct reaction to "a verdict
 * MAY have changed" is to ask the one endpoint that knows ({@code POST /auth/gates/refresh}), which compares
 * the fresh decisions against the claims in the current token and answers `{changed:false}` when nothing
 * moved. A per-reason shortcut here would be a second, weaker copy of that comparison.
 */
export const GATE_HUB_PUSH_METHOD = 'GateVerdictMayHaveChanged';

/** The hub's URL, appended to `BASE_URL`. The gateway routes `/hubs/*` (WebSocket) to the monolith. */
export const GATE_HUB_PATH = '/hubs/gates';

/**
 * The reasons the server currently sends. **Append-only**, and the client treats anything it does not know
 * exactly like something it does — see {@link GATE_HUB_PUSH_METHOD}.
 *
 * Kept as a value object rather than an enum so an unknown reason stays a plain string all the way through
 * to the log line, instead of being normalised into "unknown" and losing the one thing that would explain a
 * surprise refresh in a bug report.
 */
export const GateChangeReason = {
  /** A manager created/edited/removed a contact requirement that may cover this principal (#558). */
  ContactRequirementChanged: 'contactRequirementChanged',
  /** A link or a per-employer grant of this principal appeared, was consented to, revoked or re-verified. */
  ContactLinkChanged: 'contactLinkChanged',
  /** A manager changed a required-form requirement (#404). */
  FormRequirementChanged: 'formRequirementChanged',
  /** The company's `AccountSecurityLevel` policy moved (#282). */
  AccountSecurityPolicyChanged: 'accountSecurityPolicyChanged'
} as const;

/** A push from the hub: something happened server-side that may have moved a gate verdict. */
export interface GateHubPush {
  kind: 'push';
  /** One of {@link GateChangeReason}, or a value this build has never heard of. Log-only. */
  reason: string;
  /** When the server says it happened (ISO-8601), or null when the payload carried no instant. */
  occurredUtc: string | null;
}

/**
 * The connection is up — either for the first time, or again after a drop.
 *
 * Deliberately not distinguished from a first connect: a push raised while the socket was down was simply
 * never delivered (SignalR does not replay), so "we are connected again" and "we just connected" oblige the
 * client to do the same thing — ask once.
 */
export interface GateHubConnected {
  kind: 'connected';
}

export type GateHubSignal = GateHubPush | GateHubConnected;

/**
 * The slice of `signalR.HubConnection` this client uses.
 *
 * A structural interface rather than the class, for one reason that is worth the six lines: a spec must be
 * able to hand in a fake, and a fake of the real class would either open a real socket or be a cast that
 * silently stops matching when a member is added. The real `HubConnection` satisfies this as-is.
 */
export interface GateHubConnection {
  readonly state: HubConnectionState;

  on(methodName: string, newMethod: (...args: unknown[]) => void): void;

  onreconnected(callback: (connectionId?: string) => void): void;

  onclose(callback: (error?: Error) => void): void;

  start(): Promise<void>;

  stop(): Promise<void>;
}

/** Builds the connection. The one seam between this client and `@microsoft/signalr`. */
export type GateHubConnectionFactory =
  (url: string, accessTokenFactory: () => string) => GateHubConnection;

/**
 * The production factory: the same `withUrl` shape every other hub in this SPA uses.
 *
 * `accessTokenFactory` is read **per negotiate**, not captured, so a token swapped mid-session (a gate
 * reissue, a context enter) is picked up by the next reconnect without this client having to notice. The
 * `CM-Secret` cookie rides `withCredentials` — the monolith decrypts the bearer with it on every request,
 * hub negotiate included, and without it the connection is refused before the JWT is even read.
 */
export const defaultGateHubConnectionFactory: GateHubConnectionFactory =
  (url, accessTokenFactory) => new signalR.HubConnectionBuilder()
    .configureLogging(signalR.LogLevel.Warning)
    .withUrl(url, {withCredentials: true, accessTokenFactory})
    .withAutomaticReconnect()
    .build();

/** Overridable in a spec (and only there) so no test ever opens a socket. */
export const GATE_HUB_CONNECTION_FACTORY = new InjectionToken<GateHubConnectionFactory>(
  'GATE_HUB_CONNECTION_FACTORY', {providedIn: 'root', factory: () => defaultGateHubConnectionFactory});

/**
 * How long to wait before retrying an INITIAL connect that failed, and how many times.
 *
 * `withAutomaticReconnect()` covers a connection that was established and then dropped; it does **not** retry
 * a `start()` that never succeeded. That case is real and not rare — the SPA boots while the API is still
 * coming up, or an older server has no `/hubs/gates` at all. Retrying a bounded number of times covers the
 * first; giving up quietly covers the second, and the safety net (the daily watchdog and the
 * hub-down visibility backstop in `GateRefreshService`) is what keeps the feature correct without a socket.
 */
export const GATE_HUB_CONNECT_RETRY_MS: readonly number[] = [5_000, 15_000, 60_000];

/**
 * Holds the SPA's connection to the **gates hub** and turns it into two signals: "a verdict may have changed"
 * and "we are (re)connected" (#558).
 *
 * ### Why a push at all
 *
 * The three gates — account security (#282), required forms (#404), required contacts (#558) — are decided
 * when a token is minted and then ride along in it. Before this, the only thing that re-asked was a throttled
 * poll on focus, and the first manual smoke showed exactly what that costs: a manager assigned a rule and the
 * employee's open tab did nothing for five minutes, because the throttle window had been seeded by the check
 * that ran at login. The server knows the instant a requirement, a link or a policy moves; this makes it say
 * so, and reduces every other trigger to a safety net.
 *
 * ### What it never sends
 *
 * Nothing. The client invokes no hub method and passes no ids: the server derives the groups from the
 * connection's own token (company, shard, employee), exactly as `/hubs/devices` does. A client that could
 * name its own group could name somebody else's.
 *
 * ### Lifecycle
 *
 * Driven entirely by `AuthenticationService.user`, on the **principal**, not the token:
 *
 * * a session appears (login, or one restored from storage at bootstrap) → connect;
 * * the session goes away (logout, a 401 recovered by `ErrorInterceptor`) → disconnect;
 * * the principal or its tenant context changes (a different account, entering another company/shard) →
 *   reconnect, because the server's groups are derived from the token at negotiate time and would otherwise
 *   stay pointed at the previous context;
 * * the token alone changes (a gate reissue, `/auth/gates/refresh` answering `changed:true`) → **nothing**.
 *   That is the important one: reconnecting there would mean a push → refresh → new token → reconnect →
 *   refresh loop, and it buys nothing since `accessTokenFactory` reads the live token anyway.
 *
 * Owned by {@link GateRefreshService}, which starts it, consumes {@link signals} and stops it on teardown —
 * one owner, so there is never a question of which of the two tears the socket down.
 */
@Injectable({providedIn: 'root'})
export class GateHubClient implements OnDestroy {
  /** True while the hub connection is established. Read by the refresh service's degraded-mode backstop. */
  readonly connected = signal(false);

  private readonly subject = new Subject<GateHubSignal>();

  /** Pushes and (re)connections, in arrival order. Hot: a signal raised while nobody listens is lost. */
  readonly signals: Observable<GateHubSignal> = this.subject.asObservable();

  /** The live connection, or null while there is no session (or between a teardown and the next connect). */
  private connection: GateHubConnection | null = null;

  /** The `auth.user` subscription — the thing that drives the whole lifecycle. Null while stopped. */
  private sessionSubscription: Subscription | null = null;

  /** The principal currently connected for, or null. See the class remarks for why it is not the token. */
  private connectedKey: string | null = null;

  /** Pending initial-connect retry, and how many have been spent. */
  private retryTimer: ReturnType<typeof setTimeout> | null = null;
  private retryCount = 0;

  constructor(private auth: AuthenticationService, @Inject('BASE_URL') private baseUrl: string,
              @Inject(GATE_HUB_CONNECTION_FACTORY) private factory: GateHubConnectionFactory) {
  }

  /**
   * Begin following the session. Idempotent — calling it twice does not open a second connection.
   *
   * `auth.user` is a `BehaviorSubject`, so subscribing here immediately delivers whatever session the SPA
   * booted with; a tab reloaded onto a stored session connects without waiting for a login that will never
   * come.
   */
  start(): void {
    if (this.sessionSubscription) return;
    this.sessionSubscription = this.auth.user.subscribe(user => this.onSession(user));
  }

  /** Stop following the session and drop the connection. The client can be {@link start}ed again after. */
  stop(): void {
    this.sessionSubscription?.unsubscribe();
    this.sessionSubscription = null;
    this.connectedKey = null;
    this.teardownConnection();
  }

  ngOnDestroy(): void {
    this.stop();
    this.subject.complete();
  }

  /** A session arrived, went away or changed identity. Reconnect only when the identity actually moved. */
  private onSession(user: UserJwt | null): void {
    const key = principalKey(user);
    if (key === this.connectedKey) return;

    this.connectedKey = key;
    this.teardownConnection();
    if (key !== null) this.openConnection();
  }

  /** Build, wire and start a connection for the current session. */
  private openConnection(): void {
    const connection = this.factory(this.baseUrl + GATE_HUB_PATH,
      () => this.auth.userValue?.jwt ?? '');
    this.connection = connection;

    connection.on(GATE_HUB_PUSH_METHOD, (...args: unknown[]) => this.onPush(args[0]));
    connection.onreconnected(() => {
      if (this.connection === connection) this.announceConnected();
    });
    connection.onclose(() => {
      if (this.connection === connection) this.connected.set(false);
    });

    connection.start().then(
      () => {
        // A session that changed while `start()` was in flight already tore this one out of `this.connection`.
        // Stopping it here is what stops a stale connection (wrong tenant groups) from delivering anything.
        if (this.connection !== connection) {
          connection.stop().catch(() => undefined);
          return;
        }
        this.retryCount = 0;
        this.announceConnected();
      },
      err => {
        if (this.connection !== connection) return;
        this.connected.set(false);
        this.scheduleConnectRetry(err);
      });
  }

  /**
   * Retry an initial connect that never succeeded, a bounded number of times, then stop trying.
   *
   * Giving up is deliberate and safe: without a socket the gates are still enforced on every request
   * server-side, and `GateRefreshService` notices the hub is down and re-arms its visibility backstop. An
   * unbounded retry against a server that simply does not have this hub would be a request every minute for
   * as long as the tab is open, for nothing.
   */
  private scheduleConnectRetry(err: unknown): void {
    if (this.retryCount >= GATE_HUB_CONNECT_RETRY_MS.length) {
      console.warn('[gate-hub] giving up on the gates hub; the refresh safety net takes over', err);
      return;
    }

    const delay = GATE_HUB_CONNECT_RETRY_MS[this.retryCount++];
    this.retryTimer = setTimeout(() => {
      this.retryTimer = null;
      // Only retry while the session that asked for this connection is still the current one.
      if (this.connectedKey !== null && this.connection !== null) this.openConnection();
    }, delay);
  }

  /** Publish "connected", the trigger for a catch-up refresh (a push may have been missed while down). */
  private announceConnected(): void {
    this.connected.set(true);
    this.subject.next({kind: 'connected'});
  }

  /** Turn whatever the server sent into a {@link GateHubPush}. Shape-tolerant on purpose — see {@link readPush}. */
  private onPush(payload: unknown): void {
    this.subject.next(readPush(payload));
  }

  /** Stop and forget the current connection, and cancel a pending retry. Safe to call when there is none. */
  private teardownConnection(): void {
    if (this.retryTimer !== null) clearTimeout(this.retryTimer);
    this.retryTimer = null;
    this.retryCount = 0;

    const connection = this.connection;
    this.connection = null;
    this.connected.set(false);
    if (connection && connection.state !== HubConnectionState.Disconnected) {
      connection.stop().catch(err => console.warn('[gate-hub] stop failed', err));
    }
  }
}

/**
 * What identifies the principal a connection was opened for — **not** the token.
 *
 * The server derives the hub's groups from the token at negotiate time, so the connection has to be rebuilt
 * whenever the account or the tenant context behind it changes, and must NOT be rebuilt when only the token
 * string moved (see the class remarks). `contextEmployeeId` is in the key because it is the one field that
 * tells two bracketed employees of the same company apart — their company, shard and subject are identical.
 *
 * @returns the key, or null when there is no usable session.
 */
export function principalKey(user: UserJwt | null): string | null {
  if (!user?.jwt) return null;
  return [user.id ?? '', user.company?.id ?? '', user.shard?.id ?? '', user.contextEmployeeId ?? ''].join('|');
}

/**
 * Read a push payload defensively.
 *
 * SignalR delivers method arguments positionally and the server is free to send the DTO as one object or the
 * reason as a bare string; both are plausible spellings of the same contract and the difference is invisible
 * until a push silently stops triggering anything. Neither shape carries a decision — the reason is logged,
 * never branched on — so accepting both costs nothing and removes a whole class of "the push arrived and the
 * tab did not move".
 */
export function readPush(payload: unknown): GateHubPush {
  if (typeof payload === 'string') return {kind: 'push', reason: payload, occurredUtc: null};

  const record = (payload ?? {}) as Record<string, unknown>;
  const reason = typeof record['reason'] === 'string' ? record['reason'] as string : 'unspecified';
  const occurredUtc = typeof record['occurredUtc'] === 'string' ? record['occurredUtc'] as string : null;
  return {kind: 'push', reason, occurredUtc};
}
